import { messengerStore$ } from "@/app/(normal)/messenger/messenger.store"
import { chatStore$ } from "@/lib/ContactsMap"
import { cyrb64Hash } from "@/lib/crypto.utils"
import { InteractionSchema } from "@/schemas/contacts"
import { dynamoStore$ } from "@/stores/dynamo.v2.store"
import { syncStore$ } from "@/stores/sync.v2.store"
import { fetchMapKeyData } from "@/sync/sync"
import type { IRegisterStreamProps } from "@/sync/syncTypes"
import consola from "consola"

export const RecentCallsStreamHandler = (name: string): IRegisterStreamProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Map ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Stream removed: ${name}`)
  },
  onMessagePublished: async (data: unknown, rawData: unknown) => {
    consola.debug(`Stream message received from ${name}`, data, rawData)
    const parsedItem = InteractionSchema.safeParse(data)
    if (!parsedItem.success) {
      consola.error(`We will ignore the update from ${name} because the data is invalid`, parsedItem.error)
      return
    }

    const parsedData = parsedItem.data
    if (!parsedData?.ContactPersonId) {
      consola.error(`We will ignore the update from ${name} because the ContactPersonId is missing`)
    }

    // Ignore if it is not from the current user or to the current user
    let ignoreAddingContact = false
    const myUserID = (syncStore$.userStatus.id.peek() ?? -1).toString()
    if (parsedData.ContactPersonId !== myUserID && parsedData.UserPersonId !== myUserID) {
      ignoreAddingContact = true
    }

    parsedData.Hash = cyrb64Hash(JSON.stringify(data))

    const isNewContact = dynamoStore$.recentUserContacts.dataMap[`${parsedData.ContactPersonId}`].get() === undefined
    console.log("isNewContact", isNewContact)
    // If it is a new contact (for example, if it is from an incoming call)
    // we need to get its data from the server
    if (isNewContact) {
      consola.debug("Received a new contact", parsedData.ContactPersonId)
      consola.debug("Fetching contact data from the sync...")
      await fetchMapKeyData({
        key: `${parsedData.ContactPersonId}`,
        map: "contacts",
      })
      consola.debug("Contact data fetched from the sync")
    }
    //

    // const index = chatStore$.interactions[`${parsedData.ContactPersonId}`].findIndex(
    //   (i) => i.RingId.peek() === parsedData.RingId
    // )

    // Check if there is already an interaction with the same ringId
    // and if so update it
    // If not, add a new interaction at the beginning of the array

    const contactPersonID = parsedData.ContactPersonId

    if (!contactPersonID) {
      consola.warn("[recentCalls.handler] No contactPersonID found, skipping")
      return
    }

    console.debug("[recentCalls.handler] contactPersonID", contactPersonID)

    if (parsedData.RingId && !chatStore$.interactions[contactPersonID][parsedData.RingId].peek()) {
      // If we don't have it, we save it
      chatStore$.interactions[contactPersonID][parsedData.RingId!].set(parsedData)

      // Add it to the array of interactions
      const currentItems = chatStore$.sortedInteractions[contactPersonID].get() ?? []

      chatStore$.sortedInteractions[contactPersonID].set([...currentItems, parsedData.RingId])
      chatStore$._internal.sortedInteractionsMap[contactPersonID][parsedData.RingId].set(true)

      if (ignoreAddingContact === false) {
        // Set this as the last interaction
        chatStore$.chatLastInteraction[contactPersonID].set(parsedData)

        // Move this contact to the top of the chats list
        // We only apply this logic here, and not if the event is an interaction
        // update because we want to keep the chat list sorted by the last interaction
        if (!chatStore$._internal.chatsMap[contactPersonID].peek()) {
          // It should be added to the chat list but at the top (index 0)
          // since it is a interaction emited right now!
          chatStore$.chats.unshift(contactPersonID)
          chatStore$._internal.chatsMap[contactPersonID].set(true)
        } else {
          // If it is, we need to move it to the top
          const arrWithoutContact = chatStore$.chats.peek().filter((contactID) => contactID !== contactPersonID)
          chatStore$.chats.set([contactPersonID, ...arrWithoutContact])
        }

        // Since it is a new interaction, let's see if we have to update the unread chats counter
        // Check first the interaction direction
        const isIncoming = parsedData.Direction === "in"

        if (window.location.pathname.startsWith("/messenger") === false && isIncoming) {
          // If we are in the messenger screen, we don't want to update the unread chats counter
          // Also, if it is not an incoming interaction, we don't count it as unread
          // Otherwise, let's check if the contact already has an unread chat
          if (!chatStore$.unreadChats[contactPersonID].peek()) {
            // If it doesn't, we set it to 1
            chatStore$.unreadChats[contactPersonID].set(1)
          } else {
            // If it does, we increment it by 1
            chatStore$.unreadChats[contactPersonID].set(chatStore$.unreadChats[contactPersonID].peek()! + 1)
          }
        }

        // If it is a new interaction, it is a SMS or MMS and it is outgoing, we updated the selected contact to the contactPersonId
        if (parsedData.Direction === "ou" && parsedData.Type === "message") {
          messengerStore$.selectedContactID.set(contactPersonID)
        }

        // If it is a new interaction, it is a call and it is incoming, we update the selected contact to the contactPersonId
        if (parsedData.Direction === "in" && (parsedData.Type === "call" || parsedData.Type === "liveCall" || parsedData.Type === "video" || parsedData.Type === "departmentCall" || parsedData.Type === "personalCall")) {
          messengerStore$.selectedContactID.set(contactPersonID)
        }
      }

      consola.debug("New interaction", parsedData)
    } else {
      // In any case, we need to update its data
      chatStore$.interactions[contactPersonID][parsedData.RingId!].set(parsedData)

      // If we have it, we need to check if it is in the array or push it
      if (!chatStore$._internal.sortedInteractionsMap[contactPersonID][parsedData.RingId!].peek()) {
        // If it was not added, add it
        const currentItems = chatStore$.sortedInteractions[contactPersonID].get() ?? []

        chatStore$.sortedInteractions[contactPersonID].set([...currentItems, parsedData.RingId!])
        chatStore$._internal.sortedInteractionsMap[contactPersonID][parsedData.RingId!].set(true)
      }

      consola.debug("Updated interaction", parsedData)
    }

    // In any case, if it is an outgoing call, we update the selected contact to the contactPersonId
    if (ignoreAddingContact === false) {
      if (parsedData.Direction === "ou" && (parsedData.Type === "call" || parsedData.Type === "liveCall" || parsedData.Type === "video" || parsedData.Type === "departmentCall" || parsedData.Type === "personalCall")) {
        // Check if the interaction already exists
        const interaction = chatStore$.interactions[contactPersonID][parsedData.RingId!].peek()
        if (!interaction) {
          messengerStore$.selectedContactID.set(contactPersonID)
        }
      }
    }

    consola.debug(`Updated state from ${name}`)
  },
})
